// Swatch interaction, image hover, responsive swatch display, and quick-add variant pre-selection
// for product cards on collection pages. Uses event delegation since cards can be dynamically
// rendered (AJAX pagination, tab-collection switches, quick-add).

class ProductGridItem {
  private initialized = false;

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.initSwatchInteractions();
    this.initImageHover();
    this.initVariantLinks();
    this.initQuickAdd();
    this.initSwatchDisplay();
  }

  // Swatch hover → swap main image, leave → revert, click → navigate to variant
  private initSwatchInteractions() {
    document.addEventListener("mouseenter", (e) => {
      const swatch = (e.target as HTMLElement).closest?.(
        ".product-item__swatched__holder .swatch--other"
      ) as HTMLElement | null;
      if (!swatch) return;

      const card = swatch.closest(".product-item") as HTMLElement | null;
      if (!card) return;

      const mainImage = card.querySelector(".product-item__bg") as HTMLElement | null;
      if (!mainImage || !swatch.dataset.variantImage) return;

      const currentSwatch = card.querySelector(".swatch--current") as HTMLElement | null;
      if (currentSwatch && swatch.dataset.variantTitle === currentSwatch.dataset.variantTitle) return;

      const fullUrl = swatch.dataset.variantImage.startsWith("//")
        ? swatch.dataset.variantImage
        : "//" + swatch.dataset.variantImage;
      mainImage.style.backgroundImage = `url("${fullUrl}")`;
    }, true);

    document.addEventListener("mouseleave", (e) => {
      const me = e as MouseEvent;
      const swatch = (me.target as HTMLElement).closest?.(
        ".product-item__swatched__holder .swatch--other"
      ) as HTMLElement | null;
      if (!swatch) return;

      // Ignore child-to-child transitions within the swatch
      if (swatch.contains(me.relatedTarget as Node)) return;

      const card = swatch.closest(".product-item") as HTMLElement | null;
      if (!card) return;

      const mainImage = card.querySelector(".product-item__bg") as HTMLElement | null;
      if (!mainImage) return;

      const originalBgSet = mainImage.dataset.bgset;
      if (!originalBgSet) return;

      const betterUrl = getBetterImageUrl(originalBgSet);
      if (betterUrl) {
        const fullUrl = betterUrl.startsWith("//") ? betterUrl : "//" + betterUrl;
        mainImage.style.backgroundImage = `url("${fullUrl}")`;
      }
    }, true);

    document.addEventListener("click", (e) => {
      const swatch = (e.target as HTMLElement).closest?.(
        ".product-item__swatched__holder .swatch--other"
      ) as HTMLElement | null;
      if (!swatch) return;

      e.preventDefault();
      e.stopPropagation();
      if (swatch.dataset.variantUrl) {
        window.location.href = swatch.dataset.variantUrl;
      }
    });
  }

  // Show second product image on hover over the image container
  private initImageHover() {
    document.addEventListener("mouseenter", (e) => {
      const container = (e.target as HTMLElement).closest?.(
        ".product-item__image"
      ) as HTMLElement | null;
      if (!container) return;

      const card = container.closest(".product-item");
      if (!card) return;

      const hoverImage = card.querySelector(".product-item__bg--hover") as HTMLElement | null;
      if (!hoverImage) return;

      const mainImage = card.querySelector(".product-item__bg") as HTMLElement | null;
      if (mainImage) mainImage.style.opacity = "0";
      hoverImage.style.opacity = "1";
    }, true);

    document.addEventListener("mouseleave", (e) => {
      const me = e as MouseEvent;
      const container = (me.target as HTMLElement).closest?.(
        ".product-item__image"
      ) as HTMLElement | null;
      if (!container) return;

      // Ignore child-to-child transitions within the image container
      if (container.contains(me.relatedTarget as Node)) return;

      const card = container.closest(".product-item");
      if (!card) return;

      const hoverImage = card.querySelector(".product-item__bg--hover") as HTMLElement | null;
      if (!hoverImage) return;

      const mainImage = card.querySelector(".product-item__bg") as HTMLElement | null;
      if (mainImage) mainImage.style.opacity = "1";
      hoverImage.style.opacity = "0";
    }, true);
  }

  // Append ?variant= to product links so each card links to the correct variant
  private initVariantLinks() {
    document.querySelectorAll<HTMLElement>(".product-item[data-variant-id]").forEach((card) => {
      const variantId = card.dataset.variantId;
      if (!variantId) return;

      card.querySelectorAll<HTMLAnchorElement>(".product-link").forEach((link) => {
        const href = link.getAttribute("href");
        if (href && !href.includes("variant=")) {
          link.setAttribute("href", href + "?variant=" + variantId);
        }
      });
    });
  }

  // Pre-select the displayed variant when quick-add modal opens
  private initQuickAdd() {
    // Intercept quick-add button clicks via delegation
    document.addEventListener("click", (e) => {
      const button = (e.target as HTMLElement).closest?.(
        "[data-quick-add-label]"
      ) as HTMLElement | null;
      if (!button) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = button.dataset.variantId;
      const productId = button.dataset.productId;

      if (variantId) {
        document.dispatchEvent(
          new CustomEvent("quickAddOpened", {
            detail: { button, variantId, productId },
          })
        );
      }
    });

    // Handle the quickAddOpened event to set the correct variant
    document.addEventListener("quickAddOpened", ((e: CustomEvent) => {
      const button = e.detail?.button as HTMLElement | undefined;
      if (!button) return;

      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const holder = button.closest(
        `[data-quick-add-holder][data-variant-id="${variantId}"]`
      );
      if (!holder) return;

      // Wait for the theme's AJAX handler to inject the quick-add form into the holder
      setTimeout(() => {
        const formHolder = holder.querySelector("[data-quick-add-form-holder]");
        if (!formHolder) return;

        const variantInput = formHolder.querySelector(
          'input[name="id"]'
        ) as HTMLInputElement | null;
        if (variantInput && variantInput.value !== variantId) {
          variantInput.value = variantId;
          variantInput.dispatchEvent(new Event("change", { bubbles: true }));
        }

        const form = formHolder.querySelector("form[data-product-form]");
        if (form) {
          form.dispatchEvent(
            new CustomEvent("variant:change", {
              detail: { variantId: Number(variantId) },
              bubbles: true,
            })
          );
        }
      }, 100);
    }) as EventListener);
  }

  // Hide extra swatches on mobile, show "+N Colors" count
  private initSwatchDisplay() {
    handleSwatchDisplay();
    window.addEventListener("resize", handleSwatchDisplay);
  }
}

function handleSwatchDisplay() {
  // Matches the 'small-down' breakpoint: max-width 750px ($grid-medium - 1)
  const isMobile = window.innerWidth <= 750;
  const maxSwatchesMobile = 3;

  document.querySelectorAll(".product-item__swatched__holder").forEach((holder) => {
    const otherSwatches = holder.querySelectorAll(".swatch--other") as NodeListOf<HTMLElement>;
    const moreText = holder.querySelector(".swatch-more") as HTMLElement | null;

    if (moreText && !moreText.dataset.originalText) {
      moreText.dataset.originalText = moreText.textContent || "";
    }

    if (isMobile && otherSwatches.length > maxSwatchesMobile) {
      otherSwatches.forEach((swatch, index) => {
        swatch.style.display = index >= maxSwatchesMobile ? "none" : "";
      });

      if (moreText) {
        const hiddenSwatchCount = otherSwatches.length - maxSwatchesMobile;
        const originalCount =
          parseInt(moreText.dataset.originalText?.match(/\d+/)?.[0] || "0") || 0;
        const newTotal = originalCount + hiddenSwatchCount;
        moreText.textContent = `+${newTotal} Colors`;
      }
    } else {
      otherSwatches.forEach((swatch) => {
        swatch.style.display = "";
      });

      if (moreText && moreText.dataset.originalText) {
        moreText.textContent = moreText.dataset.originalText;
      }
    }
  });
}

function getBetterImageUrl(bgsetData: string): string | null {
  let match: RegExpMatchArray | null;
  if (bgsetData.includes("_540x")) {
    match = bgsetData.match(/(\S+_540x\S+)/);
  } else if (bgsetData.includes("_720x")) {
    match = bgsetData.match(/(\S+_720x\S+)/);
  } else {
    match = bgsetData.match(/(\S+)\s+\d+w/);
  }
  if (match) {
    const parts = match[1].split("//");
    return parts[parts.length - 1];
  }
  return null;
}

export const productGridItem = {
  init: () => {
    new ProductGridItem().init();
  },
};
